/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024-2025 (original work) Open Assessment Technologies SA
 */

import { log } from '@/core/utils/logger';

/**
 * @typedef {Object} Filter - ElasticSearch query filter
 * @see https://www.elastic.co/guide/en/elasticsearch/reference/current/filter-search-results.html
 * @property {string} type
 * @property {string} field
 * @property {*[]} values
 * @property {Boolean} not_included
 */

/**
 * @typedef {Object} OptionObject
 * @property {key} string - key
 * @property {label} string  - label
 */
/**
 * Returns whether a value is empty. An emtpy array or empty object are considered an empty value
 * @param {string | number | OptionObject | OptionObject[]} value
 * @returns {boolean} True if the value is empty, false otherwise
 */
export const isEmptyValue = value => {
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (value && typeof value === 'object') {
        return Object.keys(value).length === 0 && value.constructor === Object;
    }
    return value !== 0 && !value;
};
/**
 * Loops through the required filter keys and returns true if all have values
 * @param {string[]} requiredFilterKeys
 * @param {Object<string, any>} filterValues
 * @returns {boolean} True if all required filters have values
 */
export const allRequiredFieldsHaveValues = (requiredFilterKeys, filterValues) =>
    requiredFilterKeys.every(filterKey => {
        const value = filterValues[filterKey];
        return !isEmptyValue(value);
    });

/**
 * Returns the filterConfig with the specified key.
 *
 * @param {import('@/modules/reports/types.js').FilterConfig[]} filtersConfig
 * @param {string} key
 * @returns {import('@/modules/reports/types.js').FilterConfig|void} FilterConfig found. If not found returns void.
 */
export const findFilterConfig = (filtersConfig, key) => {
    if (Array.isArray(filtersConfig) && filtersConfig.length > 0) {
        for (const filterConfig of filtersConfig) {
            if (filterConfig.key === key) return filterConfig;
        }
    }
    return;
};
/**
 * Tells whether filters this field depends on are empty. This will allow this field to know if it should be disabled.
 *
 * @param {import('@/modules/reports/types.js').FilterDependsOn[]} filterDependsOn
 * @returns {boolean} True if any of the filters this field depends on has an empty value. False otherwise.
 * If the `filterDependsOn` parameter is an empty object, it returns false.
 */
export const dependsOnEmptyFilters = filterDependsOn =>
    filterDependsOn ? Object.keys(filterDependsOn).some(key => isEmptyValue(filterDependsOn[key]?.value)) : false;

/**
 * Creates filters for the dynamic query api lookup endpoint
 * @param {import('@/modules/reports/types.js').FilterDependsOn} filterDependsOn
 * @returns {Filter[]} Array of filters
 */
export const createLookupFilters = filterDependsOn =>
    // eslint-disable-next-line no-unused-vars
    Object.entries(filterDependsOn).map(([_, dependsOnObject]) => ({
        field: dependsOnObject.filterConfig.entityField,
        type: 'terms',
        values: Array.isArray(dependsOnObject.value)
            ? dependsOnObject.value
            : [
                  typeof dependsOnObject.value === 'object' && dependsOnObject.value
                      ? dependsOnObject.value.key
                      : dependsOnObject.value
              ]
    }));

/**
 * Creates "date after" filter for use with dynamic API requests
 * @param {string} dateFieldName
 * @param {number} date - unix timestamp in milliseconds
 * @returns {Filter}
 */
export const createDateAfterFilter = (dateFieldName, date = 0) => ({
    type: 'range',
    field: dateFieldName,
    fromValue: date,
    fromValueIncluded: true
});

/**
 * Creates "date before" filter for use with dynamic API requests
 * @param {string} dateFieldName
 * @param {number} date - unix timestamp in milliseconds
 * @returns {Filter}
 */
export const createDateBeforeFilter = (dateFieldName, date = Infinity) => ({
    type: 'range',
    field: dateFieldName,
    toValue: date,
    toValueIncluded: true
});

/**
 * Creates "date between" filter for use with dynamic API requests
 * @param {string} dateFieldName
 * @param {number} dateFrom - unix timestamp in milliseconds
 * @param {number} dateTo - unix timestamp in milliseconds
 * @returns {Filter}
 */
export const createDateRangeFilter = (dateFieldName, dateFrom, dateTo) => ({
    ...createDateAfterFilter(dateFieldName, dateFrom),
    ...createDateBeforeFilter(dateFieldName, dateTo)
});

/**
 * Creates "not exists" filter for use with dynamic API requests
 * @param {string} field
 * @returns {Filter}
 */
export const createNotExistsFilter = field => ({
    type: 'exist',
    field,
    not_included: true
});

/**
 * Associates 2 or more filters into a named group
 * @param {string} filterGroup
 * @param {Filter[]} filters
 * @returns {Filter[]}
 */
export const groupFilters = (filterGroup, filters = []) => filters.map(filter => ({ ...filter, filterGroup }));

/**
 * Creates match filter for use with dynamic API requests
 * @param {string} fieldName
 * @param {array} values - the desired values to match
 * @returns {Object}
 */
export const createMatchTermsFilter = (fieldName, values = []) => ({
    type: 'terms',
    field: fieldName,
    values
});

/**
 * Look for date pairs finishing with `From` and `To` and validate them if necessary (date with `From` suffix should be before than date with `To` suffix).
 * @example input:  {submitedDataFrom: 222, submitedDataTo: 111}, output:  {submitedDataFrom: 222, submitedDataTo: 222}
 * @param {import('@/modules/reports/types.js').FilterValues} filterData data that comes from filters when they are updated
 * @returns {import('@/modules/reports/types.js').FilterValues} the same filters data but with updated, when necessary
 * @throws {Error}
 */
export const validateFilterDateValues = filterData => {
    try {
        Object.keys(filterData).find(item => {
            // search if there is there is param that ends with `From`, meaning it is a date range. Get the prefix to search for the date pair
            const prefix = item.split(new RegExp('(?=From)'));
            if (prefix.length > 1) {
                // search if there is there is param that starts with startDate prefix and ends with `To`, meaning it is the pair endDate.
                const isPair = Object.keys(filterData).some(key => key === `${prefix[0]}To`);
                if (isPair) {
                    // validate pairs
                    if (filterData[`${prefix[0]}From`] >= filterData[`${prefix[0]}To`]) {
                        filterData[`${prefix[0]}To`] = filterData[`${prefix[0]}From`];
                    }
                }
            }
        });

        return filterData;
    } catch (error) {
        log.error('Error handling custom reports filter date values', error);
        return filterData;
    }
};
